import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateSegmentPackageDemand } from '../../lib/calculations';
import { formatPrice, formatPercent, formatNumber } from '../../lib/formatters';
import { TOOLTIP_STYLE } from '../../styles/chart-theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { PackageConfig, PnLResult } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';
import { InfoButton } from '../common/Tooltip';

interface Props {
  config: PackageConfig;
  pnl: PnLResult;
  buyerProxy: BuyerProxyResult;
}

export function MarketSizingPanel({ config, pnl, buyerProxy }: Props) {
  const isMobile = useIsMobile();
  const assumptions = useSimulatorStore(s => s.assumptions);

  const segmentData = useMemo(() =>
    calculateSegmentPackageDemand(config.price, config.enabledFeatures, assumptions.featureScaling),
    [config.price, config.enabledFeatures, assumptions.featureScaling]
  );

  const totalUnits = Math.round(pnl.s2Units);
  const grossRevenue = totalUnits * config.price;
  const netRevenue = Math.round(grossRevenue * (1 - assumptions.totalDeductionRate));

  const chartData = segmentData.map(s => ({
    name: s.segmentName.split(' ')[0],
    fullName: s.segmentName,
    units: Math.round(s.weight * assumptions.s1Leads * s.segmentRate),
    rate: s.segmentRate,
    color: s.color,
  }));

  return (
    <div className="card mb-6">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">
          Market Sizing — {config.name} ({formatPrice(config.price)})
        </h3>
        <InfoButton helpKey="combinedMarketReach" size={13} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Total Addressable</div>
          <div className="text-xl font-bold font-mono text-primary tabular-nums">{formatNumber(totalUnits)}</div>
          <div className="text-xs text-muted">S2 units/month (scenario)</div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Gross Revenue</div>
          <div className="text-xl font-bold font-mono text-primary tabular-nums">${(grossRevenue / 1000).toFixed(0)}K</div>
          <div className="text-xs text-muted">at list price/month</div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Net Revenue</div>
          <div className="text-xl font-bold font-mono text-chart-teal tabular-nums">${(netRevenue / 1000).toFixed(0)}K</div>
          <div className="text-xs text-muted">after <span className="font-mono">{formatPercent(assumptions.totalDeductionRate, 0)}</span> deductions</div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Gender Split</div>
          <div className="text-xl font-bold font-mono text-primary tabular-nums">{buyerProxy.demographics.femalePercent}% F</div>
          <div className="text-xs text-muted font-mono">{buyerProxy.demographics.malePercent}% Male</div>
        </div>
      </div>

      <div className="mb-2 text-xs font-medium text-secondary">Units by Segment</div>
      <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--color-axis-tick)' }} />
          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11, fill: 'var(--color-axis-tick)' }} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => [`${value.toLocaleString()} units`, 'Market Size']} />
          <Bar dataKey="units" radius={[0, 4, 4, 0]} maxBarSize={32}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={d.color} fillOpacity={0.8} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <p className="text-micro text-muted mt-3 italic">
        Demographic breakdowns are proportional to the buyer proxy group composition.
        Segment demand applies overall feature lifts uniformly.
      </p>
    </div>
  );
}
